/**
 * CustomizeInsightsDialog.tsx — [T262-PY][T269-PY]
 *
 * Quick-curation modal opened from the "Customize" trigger on the
 * Transaction Insights card (TransactionInsightsCard.tsx) and from the
 * "Quick Customize" button on the Widget Library page
 * (WidgetLibrary.tsx [T267-PY]). Surfaces every widget kind on the
 * `transactions` surface with an inline Radix Switch so the user can
 * toggle individual widgets without leaving the page.
 *
 * [T269-PY] Flat-list simplification — replaces T268-PY's primary tabs
 * + nested secondary tabs + Settings bulk-action tab with a single
 * vertically-scrolling list of all 9 widget kinds, grouped under 4
 * category section headers (Trends / Analysis / Patterns / Merchants).
 * "Reset to Defaults" is restored to the DialogFooter (left side, with
 * the existing "Open Insights Manager →" on the right).
 *
 * Category groupings (locked):
 *   • trends    (3) — spending-trend, category-breakdown, daily-cashflow
 *   • analysis  (3) — txn-type-breakdown, recurring-vs-oneoff, amount-histogram
 *   • patterns  (2) — day-of-week-spending, spending-heatmap
 *   • merchants (1) — top-merchants
 *
 * Data flow: `useWidgetSurfacePreferences('transactions')` is the only
 * state source — every write goes through the hook so optimistic
 * updates, rollback on error, and TanStack Query cache invalidation
 * stay consistent with `/widgets/insights`.
 *
 * @module components/dashboard/insights-manager/CustomizeInsightsDialog
 * @ticket T262-PY (initial dialog), T269-PY (flat-list simplification)
 * @see frontend/src/hooks/useWidgetSurfacePreferences.ts
 * @see frontend/src/components/dashboard/insights-manager/_shared.ts
 */

import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  Loader2,
  RotateCcw,
  SlidersHorizontal,
} from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { useWidgetSurfacePreferences } from '@/hooks/useWidgetSurfacePreferences';
import { cn } from '@/lib/utils';

/**
 * The 9 widget kinds that make up the `transactions` surface, in
 * canonical display order. Mirrors `INSIGHTS_MIRROR_DEFAULT_ORDER` in
 * `_shared.ts` byte-for-byte so the dialog toggles match what the
 * `/widgets/insights` page exposes. Each entry's `friendlyName` +
 * `description` match `InsightsGraphCard` (T246.5-FE-PAGE) — drift
 * would surface as a visible inconsistency between modal + curation page.
 */
const INSIGHTS_WIDGET_KINDS: ReadonlyArray<{
  kind: string;
  friendlyName: string;
  description: string;
}> = [
  {
    kind: 'spending-trend',
    friendlyName: 'Spending Trend',
    description: 'Tracks your net spending across the last six months.',
  },
  {
    kind: 'analytics-insights-txn-type-breakdown',
    friendlyName: 'By Transaction Type',
    description: 'Splits income vs expenses vs transfers as a donut.',
  },
  {
    kind: 'analytics-category-breakdown',
    friendlyName: 'By Category',
    description: 'Top spending categories ranked by total amount.',
  },
  {
    kind: 'analytics-insights-day-of-week-spending',
    friendlyName: 'Day of Week Spending',
    description: 'Average spend per day-of-week — find your leakiest weekday.',
  },
  {
    kind: 'analytics-insights-spending-heatmap',
    friendlyName: 'Spending Heatmap',
    description: 'A 12-week calendar heatmap of every transaction.',
  },
  {
    kind: 'analytics-insights-recurring-vs-oneoff',
    friendlyName: 'Recurring vs One-Off',
    description: 'How much of your spend is locked-in vs discretionary.',
  },
  {
    kind: 'analytics-insights-amount-histogram',
    friendlyName: 'Amount Distribution',
    description: 'Histogram of transaction sizes — spot the clusters.',
  },
  {
    kind: 'analytics-daily-cashflow',
    friendlyName: 'Daily Cashflow',
    description: 'Net cashflow line over the last 30 days.',
  },
  {
    kind: 'analytics-top-merchants',
    friendlyName: 'Top Merchants',
    description: 'Where most of your money goes, ranked by spend.',
  },
];

/**
 * [T269-PY] Category section groupings — 4 sections partitioning the 9
 * widget kinds. Replaces the prior two-level tab layout with a flat list rendered as labeled `<section>` blocks under
 * `<h3>` headings, ordered Trends → Analysis → Patterns → Merchants.
 */
const CATEGORY_SECTIONS: ReadonlyArray<{
  value: 'trends' | 'analysis' | 'patterns' | 'merchants';
  label: string;
  kinds: ReadonlyArray<string>;
}> = [
  {
    value: 'trends',
    label: 'Trends',
    kinds: ['spending-trend', 'analytics-category-breakdown', 'analytics-daily-cashflow'],
  },
  {
    value: 'analysis',
    label: 'Analysis',
    kinds: [
      'analytics-insights-txn-type-breakdown',
      'analytics-insights-recurring-vs-oneoff',
      'analytics-insights-amount-histogram',
    ],
  },
  {
    value: 'patterns',
    label: 'Patterns',
    kinds: [
      'analytics-insights-day-of-week-spending',
      'analytics-insights-spending-heatmap',
    ],
  },
  {
    value: 'merchants',
    label: 'Merchants',
    kinds: ['analytics-top-merchants'],
  },
];

export interface CustomizeInsightsDialogProps {
  /** Whether the dialog is currently open (controlled). */
  open: boolean;
  /** Fired when the dialog open state should change (overlay click, Esc, X). */
  onOpenChange: (open: boolean) => void;
}

/**
 * Quick-curation modal with a flat list of all 9 widget kinds grouped
 * under 4 category section headers. One row per widget kind with an
 * inline Switch; sections are separated by hairline dividers.
 *
 * Thin presentational wrapper around
 * `useWidgetSurfacePreferences('transactions')` — every write goes
 * through the hook so optimistic updates, rollback on error, and
 * TanStack Query cache invalidation stay consistent with the
 * `/widgets/insights` page.
 */
export function CustomizeInsightsDialog({
  open,
  onOpenChange,
}: CustomizeInsightsDialogProps): React.ReactElement {
  const { preferences, isLoading, toggle, reset, isUpdating, error } =
    useWidgetSurfacePreferences('transactions');
  const navigate = useNavigate();

  // O(1) enabled lookup per row. Empty Set during cold-cache state
  // means every widget renders in its default-on state (matches BE).
  const enabledSet = React.useMemo<ReadonlySet<string>>(() => {
    const list = preferences?.enabled ?? [];
    return new Set(list);
  }, [preferences?.enabled]);

  // Per-row toggle — fire-and-forget; hook's optimistic flow rolls
  // back on failure so we don't intercept errors here.
  const handleToggle = React.useCallback(
    (kind: string, enabled: boolean) => {
      void toggle(kind, enabled);
    },
    [toggle]
  );

  // Reset-to-defaults → POST /reset mutation; page observes via cache
  // invalidation.
  const handleReset = React.useCallback(() => {
    void reset();
  }, [reset]);

  // Close modal first so overlay doesn't double-render during route
  // transition, then navigate to /widgets/insights.
  const handleOpenManager = React.useCallback(() => {
    onOpenChange(false);
    navigate('/widgets/insights');
  }, [navigate, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-xl sm:max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="customize-insights-dialog"
      >
        <DialogHeader>
          <div className="flex items-center gap-2">
            <SlidersHorizontal
              className="h-5 w-5 text-primary shrink-0"
              aria-hidden="true"
            />
            <DialogTitle
              data-testid="customize-insights-dialog-title"
              className="leading-none"
            >
              Customize Insights
            </DialogTitle>
          </div>
          <DialogDescription
            data-testid="customize-insights-dialog-description"
            className="pt-1"
          >
            Turn individual insights widgets on or off. Changes apply
            immediately to the Insights tab on the Transactions page.
          </DialogDescription>
        </DialogHeader>

        {error && (
          <div
            className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
            role="alert"
            data-testid="customize-insights-dialog-error"
          >
            {error.message}
          </div>
        )}

        {/* [T269-PY] Flat list — all 9 widget kinds grouped under 4 category
            sections, separated by hairline dividers. */}
        <div
          className={cn(
            'flex flex-col -mx-2 overflow-hidden rounded-md border border-border/40',
            'divide-y divide-border/40'
          )}
          data-testid="customize-insights-dialog-list"
          aria-busy={isUpdating ? 'true' : 'false'}
        >
          {CATEGORY_SECTIONS.map((section, sectionIndex) => (
            <section
              key={section.value}
              role="region"
              aria-labelledby={`category-header-${section.value}`}
              data-testid={`customize-insights-dialog-category-${section.value}`}
              data-category={section.value}
            >
              {sectionIndex > 0 ? (
                <div className="border-t border-border/40" aria-hidden="true" />
              ) : null}
              <h3
                id={`category-header-${section.value}`}
                data-testid={`customize-insights-dialog-category-header-${section.value}`}
                className="text-xs uppercase tracking-wide font-medium text-muted-foreground px-3 pt-3 pb-1.5"
              >
                {section.label}
              </h3>
              {section.kinds.map((kind) => {
                const entry = INSIGHTS_WIDGET_KINDS.find((w) => w.kind === kind);
                if (!entry) return null;
                const isEnabled = enabledSet.has(entry.kind);
                return (
                  <div
                    key={entry.kind}
                    className="flex items-start gap-3 py-3 px-3 min-h-[44px]"
                    data-testid={`customize-insights-dialog-row-${entry.kind}`}
                    data-widget-kind={entry.kind}
                    data-enabled={isEnabled ? 'true' : 'false'}
                  >
                    <div className="min-w-0 flex-1">
                      <label
                        htmlFor={`customize-insights-dialog-switch-${entry.kind}`}
                        className="text-sm font-medium leading-tight cursor-pointer"
                      >
                        {entry.friendlyName}
                      </label>
                      <p className="mt-1 text-xs text-muted-foreground break-words leading-relaxed">
                        {entry.description}
                      </p>
                    </div>
                    <div className="shrink-0 flex items-center gap-2 pt-0.5">
                      {isUpdating ? (
                        <Loader2
                          className="h-4 w-4 animate-spin text-muted-foreground"
                          aria-hidden="true"
                          data-testid={`customize-insights-dialog-spinner-${entry.kind}`}
                        />
                      ) : null}
                      <Switch
                        id={`customize-insights-dialog-switch-${entry.kind}`}
                        checked={isEnabled}
                        disabled={isUpdating || isLoading}
                        onCheckedChange={(checked) =>
                          handleToggle(entry.kind, checked)
                        }
                        data-testid={`customize-insights-dialog-switch-${entry.kind}`}
                        aria-label={`Toggle ${entry.friendlyName}`}
                      />
                    </div>
                  </div>
                );
              })}
            </section>
          ))}
        </div>

        {/* DialogFooter — Reset to Defaults (left) + Open Insights Manager → (right).
            Restored to T262-PY `justify-between` layout (T268-PY moved Reset into
            the Settings tab; T269-PY restores it). */}
        <DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-between gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleReset}
            disabled={isUpdating}
            data-testid="customize-insights-dialog-reset"
            aria-label="Reset to defaults"
            className="min-h-[44px]"
          >
            <RotateCcw className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />
            Reset to Defaults
          </Button>
          <Button
            type="button"
            variant="default"
            size="sm"
            onClick={handleOpenManager}
            disabled={isUpdating}
            data-testid="customize-insights-dialog-manage"
            aria-label="Open Insights Manager"
            className="min-h-[44px]"
          >
            Open Insights Manager
            <ArrowRight className="h-3.5 w-3.5 ml-1.5" aria-hidden="true" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default CustomizeInsightsDialog;